import { Component, computed, inject, input, signal } from "@angular/core";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { filter } from "rxjs";

import { TranslateTokenPipe } from "../../../../../../../core/pipes";
import { AdaaHelper, isDsV2 } from "../../../../../../../core/utils";
import { Constants } from "../../../../../../constants/constants";
import { MetricsDetailModelType, PropTypeModelType, WorkflowDiffVersionType } from "../../../../../../models";
import { LanguageService, PropertiesService } from "../../../../../../services";
import type { ScreenSectionConfigType } from "../../wf-difference.types";

@Component({
  selector: "adaa-formula-diff-view",
  imports: [TranslatePipe, TranslateTokenPipe],
  templateUrl: "./formula-diff-view.html",
  styleUrl: "./formula-diff-view.scss",
})
export class FormulaDiffViewComponent {
  readonly languageService = inject(LanguageService);
  private _propertiesService = inject(PropertiesService);
  private _translateService = inject(TranslateService);

  isNew = input<boolean>(false);
  showOnlyNew = input.required<boolean>();
  config = input.required<ScreenSectionConfigType>();
  newData = input<WorkflowDiffVersionType>();
  oldData = input<WorkflowDiffVersionType>();
  showDIffOnly = input<boolean>(false);

  ytpCalcs = signal<PropTypeModelType[]>([]);
  title = signal<string>("");

  isDsV2 = computed(() => {
    if (this.newData()) return isDsV2(this.newData()?.planId);
    if (this.oldData()) return isDsV2(this.oldData()?.planId);
    return false;
  });
  label = computed(() => {
    const { label } = this.config();
    if (!label) return "";
    if (typeof label === "string") return label;
    return label({ isDsV2: this.isDsV2() } as never);
  });
  newMetrics = computed(() => this.newData()?.metrics as MetricsDetailModelType[]);
  oldMetrics = computed(() => this.oldData()?.metrics as MetricsDetailModelType[]);
  formulaIsDiff = computed(() => this.newData()?.formula !== this.oldData()?.formula);

  readonly isFormula = (formula?: string) => formula !== "NOFORMULAV";
  readonly getFormula = (formula?: string) => {
    if (formula === "NOFORMULAV") return "skpi.no_formula";
    return formula ?? "";
  };
  readonly getYtp = (id: number) => {
    const ytp = this.ytpCalcs().find((e) => e.id === id);
    return ytp ? AdaaHelper.getItemValueByToken(ytp, "name") : "";
  };
  readonly metricIsDiff = (id: number, compareWith: "new" | "old") => {
    const metrics = compareWith === "new" ? this.newMetrics() : this.oldMetrics();
    const index = metrics.findIndex((i) => i.id === id);
    if (index === -1) return "hasDiff";
    return metrics[index];
  };
  readonly isAboveAnnual = (frequency?: number) => {
    if (frequency == null) return false;
    return frequency >= Constants.FREQUENCY.ANNUAL;
  };

  public ngOnInit() {
    this._fetchConstants();
  }

  private _fetchConstants() {
    this._propertiesService
      .getPropById(Constants.CONSTANT_YTP)
      .pipe(filter((res) => !res.inError))
      .subscribe({
        next: (res) => this.ytpCalcs.set(res.responseData),
      });
  }

  public getInfo(metricType: string) {
    let title = "metrics.global";
    if (metricType === Constants.METRICS_TYPE.LOCAL) title = "metrics.local";
    else if (metricType === Constants.METRICS_TYPE.DIMENSION) title = "main_types.D";

    this.title.set(this._translateService.instant(title));
  }
}
